import { configuration } from '../rest-api/Configuration'
import {
  Configuration as MsalConfiguration,
  InteractionRequiredAuthError,
  IPublicClientApplication,
  PublicClientApplication,
  RedirectRequest,
} from '@azure/msal-browser'

export const msalConfig: MsalConfiguration = {
  auth: {
    clientId: configuration.AppRegistrationAppId,
    redirectUri: window.location.origin,
    postLogoutRedirectUri: window.location.origin,
    navigateToLoginRequestUrl: true,
    authority: 'https://login.microsoftonline.com/90610670-1a80-434a-88c3-e568bce39bc5',
  },
  cache: {
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: false,
  },
}

const loginRequest: RedirectRequest = {
  scopes: ["User.Read"],
  extraScopesToConsent: [
    `${configuration.SolarGisPvSpotAPI}user_impersonation`,
    `${configuration.SmartAPIScope}`
  ]
}

let _msalInstance: PublicClientApplication | undefined = undefined

export async function getMsalInstance(): Promise<IPublicClientApplication> {
  if (_msalInstance) return _msalInstance

  const msalInstance = new PublicClientApplication(msalConfig)
  await _loginRedirect(msalInstance)

  _msalInstance = msalInstance
  return msalInstance
}

async function _loginRedirect(msalInstance: IPublicClientApplication): Promise<void> {
  const authResult = await msalInstance.handleRedirectPromise()
  if (authResult?.account != null) {
    msalInstance.setActiveAccount(authResult.account)
  } else {
    await msalInstance.loginRedirect(loginRequest)
  }
}

function getAccessTokenScopes(api: 'SolarGis' | 'DowntimeApi' | 'SmartApi', marketKey: string): string[] {
  if (!api && !marketKey) {
    throw new Error("no markey key provided")
  }

  switch(api)
  {
    case 'SolarGis':
      return [configuration.SolarGisPvSpotAPI + "user_impersonation"]
    case 'DowntimeApi':
      return [configuration.DowntimeAPIScope(marketKey)]
    case 'SmartApi':
      return [configuration.SmartAPIScope]
  }
}

export async function getAccessToken(api: 'SolarGis' | 'DowntimeApi' | 'SmartApi', marketKey: string): Promise<string> {
  const msalInstance = await getMsalInstance()
  const account = msalInstance.getActiveAccount()
  if (!account) {
    return Promise.reject()
  }

  const accessTokenScopes = getAccessTokenScopes(api, marketKey)

  try {
    const result = await msalInstance.acquireTokenSilent({ account, scopes: accessTokenScopes })
    return result.accessToken;
  } catch (e) {
    console.warn("Failed to get access token", e)
    if (typeof e !== "object" || e == null) {
      throw e
    }

    if (e instanceof InteractionRequiredAuthError || e['errorCode'] === "login_required") {
      await _loginRedirect(msalInstance)
    } else if (e['errorCode'] !== "invalid_client") {
      await msalInstance.acquireTokenRedirect({ account, scopes: accessTokenScopes })
    }

    throw e
  }
}

export async function getAccountName(): Promise<string> {
  const msalInstance = await getMsalInstance()
  return msalInstance.getActiveAccount()!.name!
}
